// AssignScribeModal.jsx
import { useState, useEffect, useRef } from 'react';

export function AssignScribeModal({ session, assignableUsers = [], sessions = [], onClose, onAssign, onReassign, onRequestExtension }) {
  const [selectedScribe, setSelectedScribe] = useState('');
  const [selectedScribeId, setSelectedScribeId] = useState('');
  const [showExtensionRequest, setShowExtensionRequest] = useState(false);
  const [extensionReason, setExtensionReason] = useState('');
  const [extensionDays, setExtensionDays] = useState(1);
  const [scribeWorkloads, setScribeWorkloads] = useState({});
  const [isScrolled, setIsScrolled] = useState(false);
  
  const modalContentRef = useRef(null);
  const scribeListRef = useRef(null);

useEffect(() => {
  if (assignableUsers.length > 0 && sessions.length > 0) {
    const workloads = {};
    
    assignableUsers.forEach(scribe => {
      const tasksByName = sessions.filter(s => 
        s.assignedTo !== '-' && 
        s.assignedTo === scribe.name && 
        !s.status.includes('Finalized') && 
        !s.status.includes('Approved') && 
        !s.status.includes('Signed')
      ).length;
      
      const tasksById = sessions.filter(s => 
        s.assignedToId && 
        s.assignedToId === scribe.userId && 
        !s.status.includes('Finalized') && 
        !s.status.includes('Approved') && 
        !s.status.includes('Signed')
      ).length;
      
      workloads[scribe.userId] = Math.max(tasksByName, tasksById);
    });
    
    setScribeWorkloads(workloads);
  }
}, [assignableUsers, sessions]);

  // Handle scroll events on modal content
  useEffect(() => {
    const handleScroll = () => {
      if (modalContentRef.current) {
        setIsScrolled(modalContentRef.current.scrollTop > 10);
      }
    };

    const contentElement = modalContentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const getScribeTaskCount = (scribeId) => {
    return scribeWorkloads[scribeId] || 0;
  };

  const handleAssignConfirm = () => {
    if (selectedScribe && selectedScribeId) {
      // Prevent re-assigning to the same scribe
      if (session.assignedTo !== '-' && selectedScribe === session.assignedTo) {
        alert('Cannot re-assign to the same scribe!');
        return;
      }
      
      if (session.assignedTo === '-') {
        onAssign(session.id, selectedScribe);
      } else {
        onReassign(session.id, selectedScribe);
      }
      onClose();
    }
  };

  const handleExtensionSubmit = () => {
    if (extensionReason.trim()) {
      onRequestExtension(session.id, extensionDays, extensionReason);
      onClose();
    }
  };

  // Calculate due date with extension
  const calculateExtendedDueDate = () => {
    if (!session.dueDate) return null;
    const dueDate = new Date(session.dueDate);
    dueDate.setDate(dueDate.getDate() + extensionDays);
    return dueDate.toISOString().slice(0, 10);
  };

 // In AssignScribeModal.jsx, update the getAvailabilityBadge function:
const getAvailabilityBadge = (scribe) => {
  const taskCount = getScribeTaskCount(scribe.userId);
  
  // Use scribe.capacity if available, otherwise default to 50
  const capacity = scribe.capacity || 50;
  
  // Handle case where availability might not be set
  const scribeAvailability = scribe.availability || 'Available';
  
  // Check for unavailable status first
  if (scribeAvailability === 'Unavailable') {
    return { 
      text: 'Unavailable', 
      color: 'bg-red-100 text-red-800 border-red-200',
      description: 'Currently not accepting new tasks' 
    };
  }
  
  // Calculate capacity percentage, handle division by zero
  const capacityPercent = capacity > 0 ? (taskCount / capacity) * 100 : 0;
  
  // Rest of the function remains the same...
  if (capacityPercent >= 100) {
    return { 
      text: 'Overloaded', 
      color: 'bg-red-100 text-red-800 border-red-200',
      description: `${taskCount} active tasks (over capacity)` 
    };
  } else if (capacityPercent >= 80) {
    return { 
      text: 'Very Busy', 
      color: 'bg-red-100 text-red-800 border-red-200',
      description: `${taskCount} active tasks (${capacityPercent.toFixed(0)}% capacity)` 
    };
  } else if (capacityPercent >= 60) {
    return { 
      text: 'Busy', 
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      description: `${taskCount} active tasks (${capacityPercent.toFixed(0)}% capacity)` 
    };
  } else if (capacityPercent >= 40) {
    return { 
      text: 'Moderate', 
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      description: `${taskCount} active tasks (${capacityPercent.toFixed(0)}% capacity)` 
    };
  } else if (capacityPercent >= 20) {
    return { 
      text: 'Available', 
      color: 'bg-green-100 text-green-800 border-green-200',
      description: `${taskCount} active tasks (${capacityPercent.toFixed(0)}% capacity)` 
    };
  } else {
    return { 
      text: 'Highly Available', 
      color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      description: `${taskCount} active tasks (${capacityPercent.toFixed(0)}% capacity)` 
    };
  }
};

  // Function to handle scroll to top
  const scrollToTop = () => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Check if a scribe is the currently assigned scribe
  const isCurrentlyAssignedScribe = (scribeName) => {
    return session.assignedTo !== '-' && scribeName === session.assignedTo;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      {/* Scroll-to-top button */}
      {isScrolled && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-24 right-6 z-40 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-200 animate-bounce"
          style={{ animationDuration: '2s' }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}

      <div className="max-w-2xl w-full mx-auto my-auto">
        <div className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden flex flex-col max-h-[90vh]">
          {/* Modal Header - Fixed */}
          <div className="flex-shrink-0">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-5">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold">
                    {session.assignedTo === '-' ? 'Assign Session to Scribe' : 'Re-assign Session'}
                  </h3>
                  <div className="mt-1 sm:mt-2 space-y-0.5 sm:space-y-1">
                    <p className="text-blue-100 text-sm">
                      <span className="font-medium">Session:</span> {session.sessionId || session.id}
                    </p>
                    <p className="text-blue-100 text-sm">
                      <span className="font-medium">Patient:</span> {session.patientName}
                    </p>
                    <p className="text-blue-100 text-sm">
                      <span className="font-medium">Status:</span> {session.status}
                    </p>
                    {session.assignedTo !== '-' && (
                      <p className="text-blue-100 text-sm">
                        <span className="font-medium">Currently Assigned:</span> {session.assignedTo}
                      </p>
                    )}
                  </div>
                </div>
                <button 
                  onClick={onClose}
                  className="text-white hover:bg-white/20 p-1 sm:p-2 rounded-lg transition-colors ml-2"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Current Assignment Info */}
            {session.assignedTo !== '-' && (
              <div className="bg-amber-50 border-y border-amber-200 px-4 sm:px-5 py-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-amber-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-amber-800 text-sm sm:text-base">
                      Currently assigned to <span className="font-bold truncate">{session.assignedTo}</span>
                    </p>
                    {session.assignedDate && (
                      <p className="text-xs sm:text-sm text-amber-600 mt-0.5">
                        Assigned on {session.assignedDate}
                      </p>
                    )}
                    <p className="text-xs sm:text-sm text-amber-700 font-medium mt-1">
                      Note: Cannot re-assign to the same scribe
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Modal Body - Scrollable */}
          <div 
            ref={modalContentRef}
            className="flex-1 overflow-y-auto"
          >
            <div className="p-4 sm:p-5 space-y-4 sm:space-y-5">
              {/* Scribe Selection */}
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-800">
                      {session.assignedTo === '-' ? 'Select Scribe' : 'Select New Scribe'}
                    </h4>
                    <div className="text-xs text-gray-500">
                      {assignableUsers.length} scribe{assignableUsers.length !== 1 ? 's' : ''} available
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-3">
                    {session.assignedTo === '-' 
                      ? 'Choose a scribe to assign this session. Click on a scribe row to select.'
                      : 'Choose a different scribe to re-assign this session.'}
                  </p>
                  
                  {/* Availability Legend - Compact */}
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
                    <div className="flex items-center gap-1 text-xs">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <span className="text-gray-500">Highly Available</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-gray-500">Available</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      <span className="text-gray-500">Busy</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span className="text-gray-500">Overloaded</span>
                    </div>
                  </div>
                </div>

                {/* Scribe Selection Cards/List - With scroll and sticky header */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 sticky top-0 z-10">
                    <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-600">
                      <div className="col-span-1"></div>
                      <div className="col-span-5">Scribe</div>
                      <div className="col-span-3">Availability</div>
                      <div className="col-span-3 text-right">Tasks</div>
                    </div>
                  </div>
                  
                  {/* Scrollable scribe list */}
                  <div 
                    ref={scribeListRef}
                    className="max-h-[280px] overflow-y-auto"
                    style={{
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#CBD5E1 #F1F5F9',
                    }}
                  >
                    <style jsx>{`
                      div::-webkit-scrollbar {
                        width: 8px;
                      }
                      div::-webkit-scrollbar-track {
                        background: #F1F5F9;
                        border-radius: 4px;
                      }
                      div::-webkit-scrollbar-thumb {
                        background: #CBD5E1;
                        border-radius: 4px;
                      }
                      div::-webkit-scrollbar-thumb:hover {
                        background: #94A3B8;
                      }
                    `}</style>
                    
                    {assignableUsers.length > 0 ? (
                      assignableUsers.map((scribe) => {
                        const taskCount = getScribeTaskCount(scribe.userId);
                        const capacity = scribe.capacity || 50;
                        const capacityPercent = Math.min(Math.round((taskCount / capacity) * 100), 100);
                        const availability = getAvailabilityBadge(scribe);
                        const isCurrentlyAssigned = isCurrentlyAssignedScribe(scribe.name);
                        const isOverloaded = taskCount >= capacity;

                        return (
                          <div
                            key={scribe.userId}
                            title={
                              isOverloaded
                                ? "This scribe has reached capacity and cannot be assigned"
                                : ""
                            }
                            className={`border-b border-gray-100 px-4 py-3 transition-all group ${
                              isCurrentlyAssigned
                                ? "bg-gray-100 cursor-not-allowed"
                                : selectedScribeId === scribe.userId
                                  ? "bg-blue-50 ring-1 ring-blue-200 cursor-pointer"
                                  : "hover:bg-blue-50 cursor-pointer"
                            } ${scribe.availability === "Unavailable" ? "opacity-60" : ""}`}
                            onClick={() => {
                              if (
                                scribe.availability !== "Unavailable" &&
                                !isCurrentlyAssigned &&
                                !isOverloaded
                              ) {
                                setSelectedScribe(scribe.name);
                                setSelectedScribeId(scribe.userId);
                              }
                            }}
                          >
                            <div className="grid grid-cols-12 gap-2 items-center">
                              {/* Radio button */}
                              <div className="col-span-1 flex items-center">
                                <input
                                  type="radio"
                                  name="scribe"
                                  checked={selectedScribeId === scribe.userId}
                                  onChange={() => {
                                    if (
                                      scribe.availability !== "Unavailable" &&
                                      !isCurrentlyAssigned &&
                                      !isOverloaded
                                    ) {
                                      setSelectedScribe(scribe.name);
                                      setSelectedScribeId(scribe.userId);
                                    }
                                  }}
                                  disabled={
                                    scribe.availability === "Unavailable" ||
                                    isCurrentlyAssigned ||
                                    isOverloaded
                                  }
                                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                />
                              </div>

                              {/* Scribe name and ID */}
                              <div className="col-span-5 min-w-0">
                                <div className="flex items-center gap-2">
                                  <div
                                    className={`font-medium truncate text-sm ${
                                      isCurrentlyAssigned
                                        ? "text-gray-500"
                                        : selectedScribeId === scribe.userId
                                          ? "text-blue-700"
                                          : "text-gray-900 group-hover:text-blue-700"
                                    }`}
                                  >
                                    {scribe.name}
                                    {isCurrentlyAssigned && (
                                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                                        Current
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  ID: {scribe.userId}
                                </div>
                                <div
                                  className="text-xs text-gray-500 truncate mt-1"
                                  title={scribe.email}
                                >
                                  {scribe.email}
                                </div>
                              </div>

                              {/* Availability badge */}
                              <div className="col-span-3">
                                <span
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${availability.color}`}
                                >
                                  {availability.text}
                                </span>
                                {isOverloaded && (
                                  <div
                                    className="col-span-1 text-right text-red-500"
                                    title="Capacity full"
                                  >
                                    ⚠️
                                  </div>
                                )}
                              </div>

                              {/* Task count and workload bar */}
                              <div className="col-span-3 text-right">
                                <div className="text-sm font-medium text-gray-900">
                                  {taskCount}/{capacity}
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                                  <div
                                    className={`h-1.5 rounded-full transition-all duration-300 ${
                                      capacityPercent >= 100
                                        ? "bg-red-500"
                                        : capacityPercent >= 80
                                          ? "bg-red-400"
                                          : capacityPercent >= 60
                                            ? "bg-yellow-500"
                                            : capacityPercent >= 40
                                              ? "bg-blue-500"
                                              : capacityPercent >= 20
                                                ? "bg-green-500"
                                                : "bg-emerald-500"
                                    }`}
                                    style={{ width: `${capacityPercent}%` }}
                                  ></div>
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {capacityPercent}%
                                </div>
                              </div>
                            </div>

                            {/* Warning for currently assigned scribe */}
                            {isCurrentlyAssigned && (
                              <div className="mt-2 pt-2 border-t border-gray-200">
                                <div className="flex items-center gap-1 text-xs text-amber-700">
                                  <svg
                                    className="w-3 h-3 flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                    />
                                  </svg>
                                  <span>
                                    Already assigned to this session - select a
                                    different scribe
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Expanded details on selection (only if not currently assigned) */}
                            {!isCurrentlyAssigned &&
                              selectedScribeId === scribe.userId && (
                                <div className="mt-3 pt-3 border-t border-blue-100">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                                    <div>
                                      <div className="flex items-center gap-2 text-gray-600 mb-1">
                                        <svg
                                          className="w-4 h-4"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                          />
                                        </svg>
                                        <span className="font-medium">
                                          Contact:
                                        </span>
                                      </div>
                                      <div className="ml-6 space-y-1">
                                        <div className="text-gray-700">
                                          {scribe.email}
                                        </div>
                                        {scribe.phone && (
                                          <div className="text-gray-700">
                                            {scribe.phone}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-2 text-gray-600 mb-1">
                                        <svg
                                          className="w-4 h-4"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                          />
                                        </svg>
                                        <span className="font-medium">
                                          Workload Details:
                                        </span>
                                      </div>
                                      <div className="ml-6 space-y-1">
                                        <div className="text-gray-700">
                                          Capacity: {capacity} tasks
                                        </div>
                                        <div className="text-gray-700">
                                          Current: {taskCount} tasks
                                        </div>
                                        <div className="text-gray-700">
                                          After assignment: {taskCount + 1}{" "}
                                          tasks
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-8 text-center">
                        <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
                        </svg>
                        <p className="text-gray-500 text-sm">No assignable users found</p>
                        <p className="text-gray-400 text-xs mt-1">Please add scribes in the admin panel</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Scroll indicator */}
                  <div className="bg-gray-50 border-t border-gray-200 px-4 py-2 text-center">
                    <div className="text-xs text-gray-500 flex items-center justify-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                      <span>Scroll to see more scribes</span>
                    </div>
                  </div>
                </div>

                {/* Selected Scribe Summary */}
                {selectedScribe && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="bg-blue-100 p-1.5 rounded-lg">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-bold text-blue-800 text-sm">
                              Selected: <span className="text-base">{selectedScribe}</span>
                            </p>
                            {selectedScribeId && (
                              <p className="text-xs text-blue-600 mt-0.5">
                                Will have {getScribeTaskCount(selectedScribeId) + 1} total tasks
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-medium bg-white px-3 py-1 rounded-lg border border-blue-200 text-blue-700">
                        {session.assignedTo === '-' ? 'New Assignment' : 'Re-assignment'}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Rest of the modal remains the same... */}
              {/* Due Date & Extension Request */}
              {/* Information Panel */}
            </div>
          </div>

          {/* Modal Footer - Fixed */}
          <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 px-4 sm:px-5 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-xs sm:text-sm text-gray-600">
                {selectedScribe ? (
                  <span>
                    Ready to {session.assignedTo === '-' ? 'assign' : 're-assign'} to{' '}
                    <span className="font-medium text-gray-800">{selectedScribe}</span>
                  </span>
                ) : session.assignedTo !== '-' ? (
                  <span>Select a different scribe to re-assign</span>
                ) : (
                  <span>Select a scribe to continue</span>
                )}
              </div>
              <div className="flex gap-2 sm:gap-3">
                <button 
                  onClick={onClose}
                  className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm flex-1 sm:flex-none"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleAssignConfirm}
                  disabled={!selectedScribe || (session.assignedTo !== '-' && selectedScribe === session.assignedTo)}
                  className={`px-3 sm:px-5 py-2 font-medium rounded-lg transition-all text-xs sm:text-sm flex-1 sm:flex-none ${
                    !selectedScribe || (session.assignedTo !== '-' && selectedScribe === session.assignedTo)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : session.assignedTo === '-'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700'
                      : 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:from-amber-600 hover:to-orange-700'
                  }`}
                >
                  {session.assignedTo === '-' ? 'Assign Session' : 'Re-assign Session'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}